import React, { useId } from "react";
import DateField from "./DateField";
import DateTimeField from "./DateTimeField";
import {
  addDays,
  addMonths,
  browserTimeZone,
  startOfMonth,
  todayDate,
} from "./dateTimeParts";
import "./datetime.css";

/** Each preset returns the `[from, to]` date pair it stands for. */
const RANGE_PRESETS = {
  today: { label: "Today", range: (today) => [today, today] },
  "7d": { label: "Last 7 days", range: (today) => [addDays(today, -6), today] },
  "30d": {
    label: "Last 30 days",
    range: (today) => [addDays(today, -29), today],
  },
  "90d": {
    label: "Last 90 days",
    range: (today) => [addDays(today, -89), today],
  },
  mtd: {
    label: "This month",
    range: (today) => [startOfMonth(today), today],
  },
  "12m": {
    label: "Last 12 months",
    range: (today) => [addMonths(today, -12), today],
  },
  ytd: {
    label: "Year to date",
    range: (today) => [`${today.slice(0, 4)}-01-01`, today],
  },
};

/**
 * A "from → to" pair. Pairing the two inputs in one control is what makes the
 * cross-field rule (to cannot precede from) and the quick ranges possible —
 * separately rendered inputs can only validate themselves.
 *
 * In `date` mode the values are `"YYYY-MM-DD"`; in `datetime` mode they are
 * wall clocks (`"YYYY-MM-DDTHH:mm"`) and the presets cover whole days.
 */
const DateRangeField = ({
  legend,
  from,
  to,
  onChange,
  mode = "date",
  fromLabel = "From",
  toLabel = "To",
  presets = ["7d", "30d", "mtd", "90d"],
  futureAllowed = false,
  disabled = false,
  hint,
}) => {
  const reactId = useId();
  const legendId = `ksdt-range-${reactId}`;
  const isDateTime = mode === "datetime";
  const today = todayDate(browserTimeZone());

  const toDay = (value) => String(value || "").slice(0, 10);
  const asValue = (dateOnly, endOfDay) =>
    isDateTime ? `${dateOnly}T${endOfDay ? "23:45" : "00:00"}` : dateOnly;

  const applyPreset = (key) => {
    const preset = RANGE_PRESETS[key];
    if (!preset) return;
    const [start, end] = preset.range(today);
    onChange({ from: asValue(start, false), to: asValue(end, true) });
  };

  const activePreset = presets.find((key) => {
    const preset = RANGE_PRESETS[key];
    if (!preset) return false;
    const [start, end] = preset.range(today);
    return toDay(from) === start && toDay(to) === end;
  });

  const invertedRange = from && to && to < from;

  return (
    <fieldset
      className="ksdt-range"
      aria-labelledby={legend ? legendId : undefined}
    >
      {legend && (
        <legend className="ksdt-range__legend" id={legendId}>
          {legend}
        </legend>
      )}

      {presets.length > 0 && (
        <div className="ksdt__presets">
          {presets.map((key) => (
            <button
              key={key}
              type="button"
              className={`ksdt__preset${activePreset === key ? " is-active" : ""}`}
              disabled={disabled}
              aria-pressed={activePreset === key}
              onClick={() => applyPreset(key)}
            >
              {RANGE_PRESETS[key]?.label || key}
            </button>
          ))}
          {(from || to) && (
            <button
              type="button"
              className="ksdt__preset"
              disabled={disabled}
              onClick={() => onChange({ from: "", to: "" })}
            >
              Clear
            </button>
          )}
        </div>
      )}

      <div className="ksdt-range__fields">
        {isDateTime ? (
          <>
            <DateTimeField
              label={fromLabel}
              value={from}
              disabled={disabled}
              max={to || undefined}
              maxMessage={toLabel.toLowerCase()}
              onChange={(next) => onChange({ from: next, to })}
            />
            <DateTimeField
              label={toLabel}
              value={to}
              disabled={disabled}
              min={from || undefined}
              minMessage={fromLabel.toLowerCase()}
              onChange={(next) => onChange({ from, to: next })}
            />
          </>
        ) : (
          <>
            <DateField
              label={fromLabel}
              value={from}
              disabled={disabled}
              max={to || (futureAllowed ? undefined : today)}
              onChange={(next) => onChange({ from: next, to })}
            />
            <DateField
              label={toLabel}
              value={to}
              disabled={disabled}
              min={from || undefined}
              max={futureAllowed ? undefined : today}
              error={invertedRange ? `Cannot be before ${fromLabel}.` : ""}
              onChange={(next) => onChange({ from, to: next })}
            />
          </>
        )}
      </div>

      {hint && <div className="ksdt__hint">{hint}</div>}
    </fieldset>
  );
};

export default DateRangeField;
